import React, { useCallback, useEffect, useState } from "react";
import TabCards from "../../../components/tabCards";
import {
  Box,
  CardContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";

import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import dashboardService from "../../../services/DashboardServices";
import { getAdminDashboardData } from "../../../types/adminSection";
import {
  RewardApprovalPendingRow,
  RewardsRecognitionDataData,
} from "../../../types/dashboard";
import RewardAcceptanceForm from "../../successPopUp/rewardAcceptanceForm";
import { Grid } from "@mui/system";
import { useAppSelector } from "../../../globalState/hooks";

// --- Helper for Status Colors ---
const getStatusColor = (status: string) => {
  switch (String(status)) {
    case "1":
      return "orange";
    case "2":
      return "#34C759";
    case "3":
      return "#FF3B30";
    default:
      return "gray";
  }
};

const RRApprovalList: React.FC = () => {
  const navigate = useNavigate();
  const [tableData, setTableData] = useState<RewardApprovalPendingRow[]>([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [activeTab, setActiveTab] = useState(0);
  const [isRewardAcceptanceFormOpen, setIsRewardAcceptanceFormOpen] =
    useState(false);
  const [rewardAcceptanceFormData, setRewardAcceptanceFormData] =
    useState<RewardsRecognitionDataData | null>(null);
  const financialYear = useAppSelector(
    (state) =>
      state.dashboardReducer?.financialYear || { startDate: "", endDate: "" }
  );
  const fetchTableData = useCallback(
    async (overrides?: Partial<getAdminDashboardData>) => {
      const startDate = financialYear.startDate;
      const endDate = financialYear.endDate;

      const input: getAdminDashboardData = {
        pageNo: page + 1,
        pageSize,
        startDate,
        endDate,
        status: null,
        awardId: activeTab === 1 ? 3 : 2,
        categoryId: null,
        nominatedBy: null,
        ...overrides,
      };

      try {
        const response = await dashboardService.RRApprovalPendingList(input);
        setTableData(response?.data?.data?.data ?? []);
        setTotalCount(response?.data?.data?.totalRecords ?? 0);
      } catch (error) {
        console.error("Failed to fetch table data", error);
        setTableData([]);
        setTotalCount(0);
      }
    },
    [page, pageSize, activeTab]
  );

  useEffect(() => {
    fetchTableData();
  }, [activeTab, page, pageSize, fetchTableData]);

  // Handle Reward Acceptance Popup
  const fetchRewardAndRecognitionStatus = async (id: number) => {
    setIsRewardAcceptanceFormOpen(true);
    navigate(`/approvalPendingList?RewardAndRecognitionId=${id}`, {
      replace: true,
    });

    try {
      const response = await dashboardService.GetRewardAndRecognitionStatusAPI(
        id
      );
      if (response.data?.success) {
        setRewardAcceptanceFormData(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching reward status:", error);
    }
  };

  // --- Table Component ---
  const renderTable = () => (
    <CardContent sx={{ p: 0 }}>
      <TableContainer
        sx={{
          backgroundColor: "#212121",
          padding: 0,
          minHeight: "fit-content",
          borderRadius: "6px",
          overflowX: "auto",
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              {[
                "Type",
                "Nominate Employee",
                "Nominated By",
                "Nominated Date",
                "Status",
                "Action",
              ].map((header) => (
                <TableCell
                  key={header}
                  sx={{
                    color: "#fff",
                    background: "#0A0A0A",
                    fontWeight: "600",
                    padding: "12px 16px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {tableData.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={6}
                  sx={{ color: "#fff", textAlign: "center" }}
                >
                  No data available.
                </TableCell>
              </TableRow>
            ) : (
              tableData.map((row, index) => (
                <TableRow key={index} hover>
                  <TableCell sx={{ color: "#fff" }}>
                    {row.categoryType === "0"
                      ? row.crossFunctionalType
                      : row.categoryType}
                  </TableCell>

                  <TableCell
                    sx={{ color: "#fff", cursor: "pointer" }}
                    title={row.lstRewardAndRecognizationNomination_Members
                      .slice(1)
                      .map((m) => m.employeeName)
                      .join(", ")}
                  >
                    {
                      row.lstRewardAndRecognizationNomination_Members[0]
                        ?.employeeName
                    }
                    {row.lstRewardAndRecognizationNomination_Members.length >
                      1 && (
                      <>
                        {" "}
                        +{" "}
                        {row.lstRewardAndRecognizationNomination_Members
                          .length - 1}
                      </>
                    )}
                  </TableCell>

                  <TableCell sx={{ color: "#fff" }}>
                    {row.nominatedByName} ({row.nominatedBy})
                  </TableCell>

                  <TableCell sx={{ color: "#fff", whiteSpace: "nowrap" }}>
                    {row.createdAt
                      ? dayjs(row.createdAt).format("DD MMM YYYY")
                      : "-"}
                  </TableCell>

                  <TableCell
                    sx={{
                      color: getStatusColor(row.status),
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {row.statusName}
                  </TableCell>

                  <TableCell>
                    <IconButton
                      onClick={() => fetchRewardAndRecognitionStatus(row.id)}
                    >
                      <VisibilityIcon sx={{ color: "#fff" }} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Grid
        sx={{
          justifyContent: "flex-end",
          alignItems: "center",
          display: "flex",
          padding: 1,
        }}
      >
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          component="div"
          count={totalCount}
          rowsPerPage={pageSize}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setPageSize(parseInt(e.target.value, 10));
            setPage(0);
          }}
          sx={{
            color: "#ffffff",
            "& .MuiSelect-icon": { color: "#ffffff" },
            "& .MuiSvgIcon-root": { color: "#ffffff" },
          }}
        />
      </Grid>
    </CardContent>
  );

  return (
    <>
      <Box sx={{ p: 3, fontSize: "18px" }}>
        <Typography
          sx={{ fontSize: "14px", mb: 2, color: "#fff", fontWeight: "bold" }}
        >
          Reward & Recognition Approval Pending List
        </Typography>

        <TabCards
          titlePadding="0px"
          minHeight="fit-content"
          cardMaxHeight="fit-content"
          background="#212121"
          tabLabels={["RExcellence", "Cross Functional"]}
          onTabChange={setActiveTab}
        >
          {renderTable()}
          {renderTable()}
        </TabCards>
      </Box>

      {/* Reward Acceptance Popup */}
      {rewardAcceptanceFormData && (
        <RewardAcceptanceForm
          open={isRewardAcceptanceFormOpen}
          onClose={() => {
            setIsRewardAcceptanceFormOpen(false);
            navigate("/approvalPendingList");
            fetchTableData();
          }}
          onSave={() => {
            setIsRewardAcceptanceFormOpen(false);
            navigate("/approvalPendingList");
            fetchTableData();
          }}
          rewardAcceptanceFormData={rewardAcceptanceFormData}
        />
      )}
    </>
  );
};

export default RRApprovalList;
